import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { useAssistant } from '../../contexts/AssistantContext'
import { DISCLOSURE } from '../../lib/assistantCopy'
import { AssistantMark } from './AssistantMark'
import { Composer } from './Composer'
import { HowItWorksDialog } from './HowItWorks'
import { Thread } from './Thread'

/**
 * The drawer's contents (agent-1-web W2), the same in the desktop panel and
 * the phone sheet: the header with the context chip, the conversation, the
 * composer, and the one-line disclosure with "How it works".
 */
export function AssistantPanel({ variant }: { variant: 'drawer' | 'sheet' }) {
  const assistant = useAssistant()
  const [howOpen, setHowOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  // Focus on open: the composer, or the close button when the composer is
  // shut (the assistant is absent), never nothing (W2/W3 review). The phone
  // sheet repeats this after showModal(), which moves focus itself.
  useEffect(() => {
    if (rootRef.current) focusOpening(rootRef.current)
  }, [])
  if (!assistant) return null
  const hasConversation = assistant.thread.messages.length !== 0

  return (
    // A <div>, not a named <section>: the aside or the dialog around it is
    // already the "Assistant" landmark, and a second one inside would repeat it.
    <div ref={rootRef} className={`assistant-panel assistant-panel--${variant}`}>
      {variant === 'sheet' && <span className="assistant-panel__handle" aria-hidden="true" />}
      <header className="assistant-panel__head">
        <div className="assistant-panel__title-row">
          <AssistantMark size={22} />
          <h2 className="assistant-panel__title">
            Assistant
          </h2>
          <div className="assistant-panel__actions">
            {hasConversation && (
              // An icon, as the header's other actions are: the canvas draws a
              // title, a chip and two icons, and a word button crowds the chip.
              <button
                type="button"
                className="btn btn--icon"
                aria-label="New conversation"
                title="New conversation"
                disabled={assistant.thread.turn !== null}
                onClick={assistant.newConversation}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            )}
            {variant === 'drawer' && (
              <Link className="btn btn--icon" to="/assistant" aria-label="Open the full assistant page">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            )}
            <button type="button" className="btn btn--icon" aria-label="Close the assistant" onClick={assistant.closeAssistant}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
        {assistant.chip && <p className="assistant-chip-context">{assistant.chip}</p>}
      </header>

      <Thread />

      <Composer placeholder="Ask about this screen or your reading…" />

      <footer className="assistant-panel__foot">
        <p>
          {DISCLOSURE}{' '}
          <button type="button" className="link-button" onClick={() => setHowOpen(true)}>
            How it works ›
          </button>
        </p>
      </footer>
      <HowItWorksDialog open={howOpen} onClose={() => setHowOpen(false)} />
    </div>
  )
}

/** The composer if it can be used, else the panel's close button. */
// eslint-disable-next-line react-refresh/only-export-components
export function focusOpening(root: Element) {
  const input = root.querySelector<HTMLInputElement>('.assistant-composer__input:not(:disabled)')
  const close = root.querySelector<HTMLButtonElement>('button[aria-label="Close the assistant"]')
  ;(input ?? close)?.focus()
}
